/**
 * 채널 topic 어휘. 표시 순서와 표시명을 이 모듈 한 곳에서 소유한다.
 *
 * 같은 매핑을 여러 곳에 두면 화면마다 이름이 달라진다. 예: 채널 구조 카드는 `관측 벡터`,
 * 학습 제출 마법사의 요약은 `벡터`, 진단 화면은 `Observation`. 그래서 매핑을 여기에 모은다.
 *
 * 이름은 영어로 고정한다. topic 은 API, 스펙, 팀 대화가 함께 쓰는 공유 명사다.
 * 번역하면 화면 용어만 달라진다(`docs/rules.md` §11).
 *
 * **`diagnostics/diagnosticsTerms.ts` 의 `DIAGNOSTICS_GROUP_TERMS` 와 합치지 않는다.**
 * 그쪽 키는 `observation_image`·`observation_vector` 를 `observation` 하나로 합친 **렌더 그룹**
 * 키다. 그래서 이 모듈의 topic 키와 키 공간이 다르다. 두 모듈은 명사(`Action`·`Observation`)만
 * 공유한다.
 */

/**
 * 채널 topic 의 표시 순서. 순서는 다음과 같다:
 *   1. 촬영한 영상(카메라)
 *   2. 관측한 값
 *   3. 지령한 값
 *   4. 나머지
 * 소비처가 `topicOrder` 로 바꿀 수 있다.
 *
 * 학습 제출이 고르는 3종(`api/channelSelection.ts` 의 `CHANNEL_SELECTION_TOPICS`)과는 목적이 다르다.
 * 그쪽은 "제출에서 선택할 수 있는 topic" 이고, 이쪽은 "화면에 표시하는 순서"다.
 */
export const CHANNEL_TOPIC_ORDER = [
  "observation_image",
  "observation_vector",
  "action",
  "auxiliary_vector",
] as const;

type ChannelTopicTerm = {
  // 카드 제목처럼 공간이 충분한 곳에 쓰는 이름.
  label: string;
  // 한 줄 요약처럼 여러 topic 을 `·` 로 잇는 곳에 쓰는 짧은 이름. 공간이 좁아 label 을 그대로
  // 쓰면 줄이 넘친다. 그래서 별도로 둔다. 호출부가 각자 줄이지 않도록 여기서 정한다.
  short: string;
};

const CHANNEL_TOPIC_TERMS: Readonly<Record<string, ChannelTopicTerm>> = {
  observation_image: { label: "Camera", short: "Camera" },
  observation_vector: { label: "Observation Vector", short: "Vector" },
  action: { label: "Action", short: "Action" },
  auxiliary_vector: { label: "Auxiliary Vector", short: "Auxiliary" },
};

/**
 * topic 의 표시명. 모르는 topic 은 원본 값을 그대로 반환한다. 서버가 새 topic 을 추가했을 때
 * 임의의 이름을 만들면 새 topic 이 추가된 사실이 화면에 보이지 않는다.
 */
export function channelTopicLabel(topic: string): string {
  return CHANNEL_TOPIC_TERMS[topic]?.label ?? topic;
}

/** 좁은 자리용 짧은 표시명. 규칙은 `channelTopicLabel` 과 같다. */
export function channelTopicShortLabel(topic: string): string {
  return CHANNEL_TOPIC_TERMS[topic]?.short ?? topic;
}
